import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Advanced VideoHero Component
 * A resilient, browser-friendly hero video component that maximizes autoplay success
 * while gracefully degrading to user-gesture playback when policies block it.
 *
 * Features:
 * - Multiple video format support (WebM, MP4)
 * - Robust autoplay handling with fallbacks
 * - IntersectionObserver for performance
 * - Page visibility API support
 * - Accessibility and reduced motion support
 * - Mobile-first iOS Safari compatibility
 */

export type VideoSource = {
  src: string;
  type?: string; // e.g. "video/mp4", "video/webm"
};

export interface AdvancedVideoHeroProps extends React.HTMLAttributes<HTMLDivElement> {
  sources: VideoSource[];
  poster?: string;
  title: string;
  subtitle: string;
  buttonText?: string;
  buttonLink?: string;
  attemptAutoplay?: boolean;
  showOverlayControls?: boolean;
  'data-section'?: string;
  retryOnVisibility?: boolean;
  pauseWhenOffscreen?: boolean;
  onPlayError?: (err: unknown) => void;
  onAutoplay?: () => void;
  muted?: boolean;
  loop?: boolean;
  gradientOverlay?: boolean;
}

export default function AdvancedVideoHero({
  sources,
  poster,
  title,
  subtitle,
  buttonText,
  buttonLink,
  attemptAutoplay = true,
  showOverlayControls = true,
  retryOnVisibility = true,
  pauseWhenOffscreen = true,
  onPlayError,
  onAutoplay,
  muted = true,
  loop = true,
  gradientOverlay = true,
  className = '',
  ...rest
}: AdvancedVideoHeroProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [canPlay, setCanPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  }, []);

  const tryPlay = useCallback(async () => {
    const el = videoRef.current;
    if (!el) return;
    
    try {
      console.log('AdvancedVideoHero: Attempting to play video');
      
      // Critical: Set attributes before calling play()
      el.muted = muted;
      el.playsInline = true;
      // iOS Safari specific
      (el as any).webkitPlaysInline = true;

      const playPromise = el.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
      
      setIsPlaying(true);
      setBlocked(false);
      console.log('AdvancedVideoHero: Video playing successfully');
      onAutoplay?.();
    } catch (err) {
      console.log('AdvancedVideoHero: Play failed, setting up user interaction handlers:', err);
      setBlocked(true);
      setIsPlaying(false);
      onPlayError?.(err);
    }
  }, [muted, onAutoplay, onPlayError]);

  // Initial attribute setup BEFORE setting sources
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    
    console.log('AdvancedVideoHero: Setting up video attributes');
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");
    el.muted = muted;
    el.loop = loop;
    el.preload = "auto";
  }, [muted, loop]);

  // Load sources once on mount
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    
    console.log('AdvancedVideoHero: Loading video sources:', sources);
    
    // Clear any existing sources first
    while (el.firstChild) el.removeChild(el.firstChild);
    
    sources.forEach(({ src, type }) => {
      const s = document.createElement("source");
      s.src = src;
      if (type) s.type = type;
      el.appendChild(s);
      console.log('AdvancedVideoHero: Added source:', src, 'type:', type);
    });
    
    // Add error handling for source loading
    const handleSourceError = (e: Event) => {
      console.error('AdvancedVideoHero: Source failed to load:', e);
      setVideoError(true);
    };
    
    el.addEventListener('error', handleSourceError);
    el.load();
    
    return () => {
      el.removeEventListener('error', handleSourceError);
    };
  }, [sources]);

  // Autoplay attempt with multiple strategies
  useEffect(() => {
    if (!attemptAutoplay || prefersReducedMotion) return;

    const el = videoRef.current;
    if (!el) return;

    const onCanPlay = () => {
      console.log('AdvancedVideoHero: Video can play');
      setCanPlay(true);
      setVideoLoaded(true);
      setVideoError(false);
      // Try to play immediately when video is ready
      setTimeout(() => tryPlay(), 100);
    };

    const onError = (e: Event) => {
      console.error('AdvancedVideoHero: Video error:', e);
      setVideoError(true);
      setVideoLoaded(false);
    };

    const onLoadedMetadata = () => {
      console.log('AdvancedVideoHero: Video metadata loaded');
      setTimeout(() => tryPlay(), 50);
    };

    const onLoadedData = () => {
      console.log('AdvancedVideoHero: Video data loaded');
      setVideoLoaded(true);
      setTimeout(() => tryPlay(), 200);
    };

    el.addEventListener("canplay", onCanPlay, { once: true });
    el.addEventListener("error", onError);
    el.addEventListener("loadedmetadata", onLoadedMetadata, { once: true });
    el.addEventListener("loadeddata", onLoadedData, { once: true });

    // Try immediately for browsers that allow it
    tryPlay();

    return () => {
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("error", onError);
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("loadeddata", onLoadedData);
    };
  }, [attemptAutoplay, tryPlay, prefersReducedMotion]);

  // Gesture fallbacks: play on first meaningful user interaction
  useEffect(() => {
    // Always set up interaction handlers, not just when blocked
    console.log('AdvancedVideoHero: Setting up user interaction handlers');

    const trigger = () => {
      console.log('AdvancedVideoHero: User interaction detected, attempting play');
      tryPlay();
      detach();
    };

    const opts: AddEventListenerOptions = { passive: true, once: true };

    const detach = () => {
      document.removeEventListener("click", trigger);
      document.removeEventListener("touchstart", trigger);
      document.removeEventListener("keydown", trigger);
      document.removeEventListener("mousemove", trigger);
      document.removeEventListener("scroll", trigger);
      window.removeEventListener("focus", trigger);
    };

    // Add more interaction types for better coverage
    document.addEventListener("click", trigger, opts);
    document.addEventListener("touchstart", trigger, opts);
    document.addEventListener("keydown", trigger, opts);
    document.addEventListener("mousemove", trigger, opts);
    document.addEventListener("scroll", trigger, opts);
    window.addEventListener("focus", trigger, opts);

    return detach;
  }, [tryPlay]);

  // Page visibility retry
  useEffect(() => {
    if (!retryOnVisibility) return;
    
    const onVisibility = () => {
      if (document.visibilityState === "visible" && !isPlaying && !prefersReducedMotion) {
        console.log('AdvancedVideoHero: Page visible, retrying autoplay');
        tryPlay();
      }
    };
    
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [isPlaying, retryOnVisibility, tryPlay, prefersReducedMotion]);

  // Pause when offscreen using IntersectionObserver
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    
    const el = videoRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        
        if (entry.isIntersecting) {
          console.log('AdvancedVideoHero: Video in view, attempting play');
          // Always try to play when video comes into view
          setTimeout(() => tryPlay(), 300);
        } else if (pauseWhenOffscreen && !el.paused) {
          console.log('AdvancedVideoHero: Video out of view, pausing');
          el.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [pauseWhenOffscreen, tryPlay, prefersReducedMotion]);

  const toggle = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    
    if (el.paused) {
      tryPlay();
    } else {
      el.pause();
      setIsPlaying(false);
    }
  }, [tryPlay]);

  const { 'data-section': dataSection, ...sectionProps } = rest;
  
  return (
    <section
      ref={containerRef}
      className={`video-hero ${!videoLoaded ? 'video-loading' : ''} ${className}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      data-section={dataSection || 'video-hero'}
      {...sectionProps}
    >
      <video
        ref={videoRef}
        className="video-background"
        muted
        playsInline
        webkit-playsinline="true"
        loop={loop}
        poster={poster}
        style={{ 
          opacity: videoLoaded && !videoError ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out'
        }}
      />

      {/* Gradient overlay for text legibility */}
      {gradientOverlay && <div className="video-overlay" />}

      {/* Content area */}
      <div className="hero-content">
        <div className="container">
          <div 
            className="hero-title" 
            dangerouslySetInnerHTML={{ __html: title }}
          />
          <div className="hero-subtitle">{subtitle}</div>
          {buttonText && buttonLink && (
            <a href={buttonLink} className="btn btn-primary">
              {buttonText}
            </a>
          )}
        </div>
      </div>

      {/* Play/Pause overlay controls */}
      {showOverlayControls && (
        <button
          aria-label={isPlaying ? "Pause background video" : "Play background video"}
          onClick={toggle}
          className="video-control-overlay"
          style={{
            opacity: hovering || blocked ? 1 : 0,
            transition: 'opacity 0.2s ease'
          }}
        >
          {isPlaying ? '⏸️' : '▶️'}
          <span>{isPlaying ? "Pause" : "Play"}</span>
        </button>
      )}



      {/* Error display */}
      {videoError && (
        <div className="video-error">
          Video failed to load. Sources: {sources.map(s => s.src).join(', ')}
        </div>
      )}

      {/* Poster fallback */}
      {!isPlaying && poster && (
        <img
          src={poster}
          alt="Video poster"
          className="video-poster"
          loading="eager"
          aria-hidden="true"
        />
      )}
    </section>
  );
}
